import Api from '@sendsprint/api-types';
import { addTokenHeader, BusinessAxiosConfig, RequestConfig } from '@src/config/AxiosConfig';
import { BUSINESS_API_VERSION } from '@src/constants';
import ClientApi from '@src/types/client';
import { getRecipientTypeFromMapping } from '@src/utils/recipient-type';
import { AxiosResponse } from 'axios';

const formatPaymentTypeInfo = (
  paymentTypes: ClientApi.General.SettlementPaymentTypeI[]
): ClientApi.General.PaymentTypeInfo[] =>
  paymentTypes.map(({ country, id, paymentName, paymentType }) => ({
    id,
    country: country as Api.Model.CountryInitials,
    paymentName,
    paymentType: paymentType as Api.Model.PaymentType,
    status: Api.Model.Status.Active,
    recipientType: getRecipientTypeFromMapping({
      country: country as Api.Model.CountryInitials,
      paymentType: paymentType as Api.Model.PaymentType
    }) as ClientApi.General.RecipientType
  }));

/**
 * ENDPOINTS
 */

export const getBusinessCountriesService = (
  payload: ClientApi.Application.GetBusinessCountries.Request,
  config?: RequestConfig
): Promise<ClientApi.General.BusinessCountryI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetBusinessCountries.Response,
    AxiosResponse<ClientApi.Application.GetBusinessCountries.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/Application/BusinessCountries?BusinessCountry=${payload.BusinessCountry}`,
    {
      ...config,
      ...configWithToken
    }
  ).then((res) => res.data?.data?.countries || []);
};

export const getStatesByCountryService = (
  payload: ClientApi.Application.GetStatesByCountry.Request,
  config?: RequestConfig
): Promise<ClientApi.General.StateI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetStatesByCountry.Response,
    AxiosResponse<ClientApi.Application.GetStatesByCountry.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Application/StatesByCountry`, {
    ...config,
    ...configWithToken,
    params: {
      ...payload
    }
  }).then((res) => res.data?.data?.states || []);
};

export const getBusinessTypesService = (
  config?: RequestConfig
): Promise<ClientApi.General.BusinessTypeI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetBusinessTypes.Response,
    AxiosResponse<ClientApi.Application.GetBusinessTypes.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Application/BusinessTypes`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data?.businessTypes || []);
};

export const getBusinessTypesByCountryService = (
  payload: ClientApi.Application.GetBusinessTypesByCountry.Request,
  config?: RequestConfig
): Promise<ClientApi.General.BusinessTypeI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetBusinessTypesByCountry.Response,
    AxiosResponse<ClientApi.Application.GetBusinessTypesByCountry.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Application/BusinessTypesByCountry`, {
    ...config,
    ...configWithToken,
    params: { ...payload }
  }).then((res) => res.data?.data?.businessTypes || []);
};

export const getBusinessIndustriesService = (
  config?: RequestConfig
): Promise<ClientApi.General.BusinessIndustryI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetBusinessIndustries.Response,
    AxiosResponse<ClientApi.Application.GetBusinessIndustries.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Application/BusinessIndustries`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data?.businessIndustries || []);
};

export const getTransferTypesService = (
  config?: RequestConfig
): Promise<ClientApi.General.TransferTypesI[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetTransferTypes.Response,
    AxiosResponse<ClientApi.Application.GetTransferTypes.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Application/TransferTypes`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data?.transferTypes || []);
};

export const getSettlementPaymentTypesByCountryService = (
  payload: ClientApi.Application.GetSettlementPaymentTypesByCountry.Request,
  config?: RequestConfig
): Promise<ClientApi.General.PaymentTypeInfo[]> => {
  const configWithToken = addTokenHeader('');
  return BusinessAxiosConfig.get<
    ClientApi.Application.GetSettlementPaymentTypesByCountry.Response,
    AxiosResponse<ClientApi.Application.GetSettlementPaymentTypesByCountry.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/Application/SettlementPaymentTypesByCountry?CountryCode=${payload.CountryCode}`,
    {
      ...config,
      ...configWithToken
    }
  ).then((res) => formatPaymentTypeInfo(res.data?.data?.settlementPaymentTypes || []));
};
